/**
 * CollectionTypeDetail - Redesigned Collection Type Modal
 *
 * Modern, professional edit modal using EditModal with:
 * - Consistent form field styling
 * - Keyboard shortcuts
 * - Unsaved changes detection
 */

import React, { useEffect, useState, useCallback } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import {
	BaseForm,
	NumberBox,
	TextBox,
} from '../../components/base';
import { EditModal, EditPageField } from '../../components/container';
import {
	CollectionTypeModel,
	dfCollectionTypeModel,
	validCollectionTypeSchema,
} from '../../context/models/collections/CollectionTypeModel';
import { Folder, ListOrdered, Info } from 'lucide-react';

type ICollectionTypeDetailModalProps = {
	isShow: boolean;
	title: string;
	detailModel?: CollectionTypeModel;
	isSaving: boolean | false;
	onClose: () => void;
	onSubmit: (data: CollectionTypeModel) => void;
};

const CollectionTypeDetail = (props: ICollectionTypeDetailModalProps) => {
	const { isShow, title, detailModel, onSubmit, onClose, isSaving } = props;
	const isAddMode = !detailModel;
	const [initialValues, setInitialValues] = useState<CollectionTypeModel>(dfCollectionTypeModel);
	const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

	const {
		control,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<CollectionTypeModel>({
		resolver: yupResolver(validCollectionTypeSchema() as any),
		mode: 'all',
		defaultValues: initialValues,
	});

	const onSubmitHandler = (values: CollectionTypeModel) => {
		onSubmit(values);
		setHasUnsavedChanges(false);
	};

	useEffect(() => {
		if (initialValues) {
			reset(initialValues);
		}
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialValues, reset]);

	useEffect(() => {
		if (detailModel) {
			setInitialValues((prev) => ({ ...prev, ...detailModel }));
			setHasUnsavedChanges(false);
		}
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [detailModel]);

	// Helper to update field and mark as unsaved
	const updateField = useCallback((field: keyof CollectionTypeModel, value: any) => {
		setInitialValues((prev) => ({ ...prev, [field]: value }));
		setHasUnsavedChanges(true);
	}, []);

	const content = (
		<div className='space-y-4'>
			<EditPageField
				label='Số thứ tự'
				error={errors.stt?.message}
				helpText='Thứ tự hiển thị trong danh sách'
			>
				<NumberBox
					name='stt'
					error={errors.stt?.message}
					onValueChanged={(value) => updateField('stt', value)}
					value={initialValues.stt}
					hasValid={true}
				/>
			</EditPageField>

			<EditPageField
				label='Tên vị trí'
				required
				error={errors.name?.message}
			>
				<TextBox
					name='name'
					error={errors.name?.message}
					onValueChanged={(value) => updateField('name', value)}
					value={initialValues.name}
					hasValid={true}
					placeholder='Nhập tên vị trí...'
				/>
			</EditPageField>

			<EditPageField
				label='Mô tả'
				error={errors.note?.message}
				helpText='Mô tả ngắn về vị trí'
			>
				<TextBox
					name='note'
					error={errors.note?.message}
					onValueChanged={(value) => updateField('note', value)}
					value={initialValues.note}
					hasValid={true}
					placeholder='Mô tả...'
				/>
			</EditPageField>
		</div>
	);

	return (
		<EditModal
			isOpen={isShow}
			onClose={onClose}
			title={title}
			description={isAddMode ? 'Tạo mới vị trí bộ sưu tập' : 'Chỉnh sửa vị trí bộ sưu tập'}
			sizeClass='sm'
			children={content}
			isSaving={isSaving}
			onSave={handleSubmit(onSubmitHandler as any)}
			onCancel={onClose}
			hasUnsavedChanges={hasUnsavedChanges}
			breadcrumbs={[
				{ label: 'Trang chủ', href: '/' },
				{ label: 'Loại bộ sưu tập', href: '/collections-type' },
				{ label: isAddMode ? 'Tạo mới' : detailModel?.name || 'Chỉnh sửa' },
			]}
			status={isAddMode ? 'Tạo mới' : undefined}
			statusVariant='default'
		/>
	);
};

export default CollectionTypeDetail;
